import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Circle } from '@mui/icons-material'
import { Box, Grid, IconButton } from '@mui/material'
import Autoplay from 'embla-carousel-autoplay'
import useEmblaCarousel from 'embla-carousel-react'

import classes from './index.styles'

interface ICarouselProps {
  slides: React.ReactNode[]
  iconColor?: string
}

const Carousel = ({ slides, iconColor }: ICarouselProps) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ align: 'start', loop: true }, [Autoplay()])
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const [selectedSnap, setSelectedSnap] = useState<number>(0)

  const goToPrev = () => emblaApi?.scrollPrev()
  const goToNext = () => emblaApi?.scrollNext()
  const goTo = (index: number) => emblaApi?.scrollTo(index)
  const setupSnaps = useCallback(() => setScrollSnaps(emblaApi ? emblaApi.scrollSnapList() : []), [emblaApi])
  const setActiveSnap = useCallback(() => setSelectedSnap(emblaApi ? emblaApi.selectedScrollSnap() : 0), [emblaApi])

  useEffect(() => {
    if (!emblaApi) return

    setupSnaps()
    setActiveSnap()
    emblaApi.on('init', setupSnaps)
    emblaApi.on('init', setActiveSnap)
    emblaApi.on('select', setActiveSnap)
    emblaApi.plugins().autoplay?.play()
  }, [emblaApi, setActiveSnap, setupSnaps])

  return (
    <>
      <Box sx={classes.slidesContainer} ref={emblaRef}>
        <Box sx={classes.slides}>
          {slides.map((slide, index) => (
            <Box key={index} sx={classes.slide}>
              {slide}
            </Box>
          ))}
        </Box>
      </Box>

      <Grid container={true} size={12} sx={classes.controlsContainer}>
        <IconButton onClick={goToPrev} aria-label="Go to previous slide">
          <ChevronLeft sx={classes.chevronIcon(iconColor)} />
        </IconButton>

        <Grid>
          {scrollSnaps.map((_, index) => (
            <IconButton
              key={index}
              onClick={() => {
                goTo(index)
                emblaApi?.plugins().autoplay?.stop()
              }}
            >
              <Circle sx={classes.dotIcon(iconColor, selectedSnap === index)} />
            </IconButton>
          ))}
        </Grid>
        <IconButton onClick={goToNext} aria-label="Go to next slide">
          <ChevronRight sx={classes.chevronIcon(iconColor)} />
        </IconButton>
      </Grid>
    </>
  )
}

export default Carousel
